import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { LIST_LIMITS } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import {
  EmptyState,
  LoadFailed,
  NoMatches,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Input } from "@/components/ui/input";
import { runLocalQuery } from "@/modules/docs/localQuery";
import { projectSearchRow } from "@/modules/docs/projections";
import { buildSnippet, highlightSnippet } from "@/modules/docs/search";
import type { SearchRow } from "@/modules/docs/types";
import { useDocsTree } from "@/modules/docs/useDocsTree";
import type { SectionScreenProps } from "@/modules/types";

/**
 * The search input, rendered above every state below (loading, empty,
 * results) so typing a new term never remounts — and so never loses focus
 * on — the box itself.
 */
function renderSearchBox(
  term: string,
  onChange: (value: string) => void,
): ReactNode {
  return (
    <div className="relative max-w-sm shrink-0">
      <SearchIcon
        className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        aria-hidden
      />
      <Input
        className="pl-8"
        placeholder="Search titles and page content"
        value={term}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

/**
 * Full-text search across titles and bodies (FR-DOC-11).
 *
 * `docs.search` already restricts matches to pages the caller can read —
 * FR-DOC-9's filtering applies here exactly as it does to the tree. This
 * screen's own job is showing *why* each result matched, which the
 * repository call does not report; see `buildSnippet` for how that excerpt
 * is built from content already on hand.
 */
export function Search({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const treeQuery = useDocsTree();
  const [term, setTerm] = useState("");
  const trimmed = term.trim();

  const [limit, setLimit] = useState(25);
  // The term is this screen's own search box, not the toolbar's, so the trail
  // is told about it.
  const [history, setHistory] = useCursorHistory(trimmed);
  const cursor = history[history.length - 1];

  const searchQuery = useQuery({
    queryKey: ["docs", "search", trimmed],
    queryFn: () => data.docs.search(trimmed, { limit: LIST_LIMITS.docs.max }),
    enabled: trimmed !== "",
  });

  if (trimmed === "") {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3">
        {renderSearchBox(term, setTerm)}
        <EmptyState
          title="Type to search"
          description="Searches every page's title and body that you can read (FR-DOC-11)."
        />
      </div>
    );
  }

  if (treeQuery.isPending || searchQuery.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3">
        {renderSearchBox(term, setTerm)}
        <TableSkeleton />
      </div>
    );
  }

  if (treeQuery.isError) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3">
        {renderSearchBox(term, setTerm)}
        <LoadFailed
          message={treeQuery.error.message}
          onRetry={() => {
            void treeQuery.refetch();
          }}
        />
      </div>
    );
  }

  if (searchQuery.isError) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-3">
        {renderSearchBox(term, setTerm)}
        <LoadFailed
          message={searchQuery.error.message}
          onRetry={() => {
            void searchQuery.refetch();
          }}
        />
      </div>
    );
  }

  const { folders, contentByPageId } = treeQuery.data;
  const folderById = new Map(folders.map((folder) => [folder.id, folder]));

  const rows: SearchRow[] = searchQuery.data.items.map((page) => {
    const content = contentByPageId.get(page.id);
    const snippet = buildSnippet(
      trimmed,
      page.title,
      content?.revision.markdown ?? "",
    );
    return {
      page,
      folder: folderById.get(page.folderId),
      content,
      authorName: "",
      matchedIn: snippet.matchedIn,
      snippet: snippet.text,
    };
  });

  const result = runLocalQuery(
    rows,
    { filter, sort, limit, cursor },
    {
      project: projectSearchRow,
      identify: (row) => row.page.id,
      defaultSort: [{ column: "updatedAt", direction: "desc" }],
    },
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {renderSearchBox(term, setTerm)}

      <div className="flex min-h-0 flex-1 flex-col">
        <DataTable
          fields={fields}
          rows={result.items}
          getRowId={(row) => row.page.id}
          project={projectSearchRow}
          sort={sort}
          onToggleSort={onToggleSort}
          renderOverride={(field, row) => {
            if (field.name !== "context") return undefined;
            return (
              <span className="text-muted-foreground">
                {highlightSnippet(row.snippet, trimmed)}
              </span>
            );
          }}
          emptyState={
            <NoMatches
              onClear={onClearFilter}
              description={`Nothing matched "${trimmed}".`}
            />
          }
          footer={
            <Pagination
              page={result}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          }
        />
      </div>
    </div>
  );
}
